// Curated visual presets for real-estate project branding. Each preset
// bundles the design tokens that ripple across:
//   • Public viewer chrome (top bar, zone hover cards, action buttons)
//   • Letterbox fill around contained scene media
//   • Sales PDF accent rule + section markers
//
// Applying a preset **prefills** the individual color/treatment fields
// so admins can still tweak any value afterward — think of them as
// smart starting points, not locked skins.
//
// Adding a new preset is a one-liner: extend the array below. No DB
// migration required — the values flow through the existing
// `accent_color`, `letterbox_treatment`, and `pdf_template.accent_hex`
// fields on the Project document.

export const THEME_PRESETS = [
  {
    id: "luxury_dark",
    name: "Luxury Dark",
    description: "Slate + warm gold. The premium default.",
    accent_color: "#C4A47C",
    letterbox_treatment: "blurred_backdrop",
    pdf_accent_hex: "#C4A47C",
    swatch: ["#0F172A", "#C4A47C", "#E8DFCF"],
  },
  {
    id: "sand_dune",
    name: "Sand Dune",
    description: "Warm neutrals — perfect for Mediterranean coastal projects.",
    accent_color: "#B58A5E",
    letterbox_treatment: "brand_color",
    pdf_accent_hex: "#8C6239",
    swatch: ["#F5EBDC", "#B58A5E", "#8C6239"],
  },
  {
    id: "ocean",
    name: "Ocean",
    description: "Deep teal + sky. For sea-view and marina developments.",
    accent_color: "#2C7A7B",
    letterbox_treatment: "blurred_backdrop",
    pdf_accent_hex: "#1F4F52",
    swatch: ["#0B2B33", "#2C7A7B", "#A7D6D2"],
  },
  {
    id: "emerald",
    name: "Emerald",
    description: "Rich green + off-white. Forest, park-view, wellness estates.",
    accent_color: "#3A7D44",
    letterbox_treatment: "brand_color",
    pdf_accent_hex: "#245B2C",
    swatch: ["#F5F1E6", "#3A7D44", "#245B2C"],
  },
  {
    id: "rosewood",
    name: "Rosewood",
    description: "Muted plum + rose. Boutique urban residences.",
    accent_color: "#8E4560",
    letterbox_treatment: "blurred_backdrop",
    pdf_accent_hex: "#6E2E48",
    swatch: ["#231521", "#8E4560", "#E8C6D2"],
  },
  {
    id: "monochrome",
    name: "Monochrome",
    description: "Editorial black-and-white. Lets photography lead.",
    accent_color: "#111111",
    letterbox_treatment: "black",
    pdf_accent_hex: "#111111",
    swatch: ["#111111", "#4B4B4B", "#F4F4F4"],
  },
];

/** Prefill the SetupTab form state from a preset. Only the fields owned by
 *  the preset are touched — everything else the admin has already set
 *  (contact, payment plans, languages, ...) stays untouched. */
export function applyThemePreset(form, presetId) {
  const preset = THEME_PRESETS.find((p) => p.id === presetId);
  if (!preset) return form;
  return {
    ...form,
    theme: preset.id,
    accent_color: preset.accent_color,
    letterbox_treatment: preset.letterbox_treatment,
    pdf_template: {
      ...(form.pdf_template || {}),
      accent_hex: preset.pdf_accent_hex,
    },
  };
}

/** Best-effort match of the current form values back to a preset id.
 *  Used to highlight the "active" preset card in the UI. Falls back to
 *  the explicit `form.theme` if the colors have been tweaked. */
export function detectActivePreset(form) {
  const byExact = THEME_PRESETS.find(
    (p) =>
      p.accent_color.toLowerCase() === (form.accent_color || "").toLowerCase() &&
      p.letterbox_treatment === form.letterbox_treatment,
  );
  if (byExact) return byExact.id;
  return form.theme && THEME_PRESETS.some((p) => p.id === form.theme)
    ? form.theme
    : null;
}
